<template>
  <div class="back-to-top-container">
    <transition
      name="fade-slide"
      appear
    >
      <v-btn
        v-show="isVisible"
        :aria-label="$t('accessibility.back_to_top', 'Back to top of page')"
        class="back-to-top-button"
        color="accent"
        fab
        fixed
        bottom
        right
        elevation="6"
        @click="scrollToTop"
        @keydown.enter="scrollToTop"
        @keydown.space.prevent="scrollToTop"
      >
        <v-icon aria-hidden="true">
          fas fa-arrow-up
        </v-icon>

        <!-- Screen reader only text -->
        <span class="sr-only">
          {{ $t('accessibility.back_to_top_description', 'Scroll to top of page') }}
        </span>
      </v-btn>
    </transition>

    <!-- Progress indicator for long pages -->
    <div
      v-if="showProgress && isVisible"
      class="scroll-progress"
      role="progressbar"
      :aria-valuenow="scrollProgress"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="$t('accessibility.scroll_progress', 'Page scroll progress')"
    >
      <div
        class="progress-fill"
        :style="{ height: `${scrollProgress}%` }"
      />
      <span class="sr-only">
        {{ Math.round(scrollProgress) }}% scrolled
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BackToTopButton',

  props: {
    // Show button after scrolling this many pixels
    threshold: {
      type: Number,
      default: 400
    },

    // Show scroll progress indicator
    showProgress: {
      type: Boolean,
      default: true
    },

    // Smooth scroll behavior
    smoothScroll: {
      type: Boolean,
      default: true
    },

    // Scroll duration in milliseconds
    scrollDuration: {
      type: Number,
      default: 800
    }
  },

  data() {
    return {
      isVisible: false,
      scrollProgress: 0,
      isScrolling: false,
      scrollTimeout: null
    };
  },

  mounted() {
    // Add scroll listener
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Initial check
    this.handleScroll();

    // Add resize listener for progress calculation
    window.addEventListener('resize', this.calculateProgress, { passive: true });
  },

  beforeUnmount() {
    // Clean up listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.calculateProgress);

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  },

  methods: {
    /**
     * Handle scroll events
     */
    handleScroll() {
      if (this.isScrolling) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Show/hide button based on scroll position
      this.isVisible = scrollTop > this.threshold;

      // Calculate scroll progress
      this.calculateProgress();

      // Throttle scroll events
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
      }, 100);
    },

    /**
     * Calculate scroll progress percentage
     */
    calculateProgress() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;

      if (scrollHeight <= 0) {
        this.scrollProgress = 0;
        return;
      }

      this.scrollProgress = Math.min(100, Math.max(0, (scrollTop / scrollHeight) * 100));
    },

    /**
     * Scroll to top of page
     */
    scrollToTop() {
      if (this.isScrolling) return;

      this.isScrolling = true;

      // Announce action to screen readers
      this.announceScrollAction();

      if (this.smoothScroll && 'scrollTo' in window) {
        // Use native smooth scroll if available
        if (this.prefersReducedMotion()) {
          // Instant scroll for users who prefer reduced motion
          window.scrollTo(0, 0);
          this.onScrollComplete();
        } else {
          // Smooth scroll
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
          });

          // Monitor scroll completion
          this.monitorScrollCompletion();
        }
      } else {
        // Fallback: animated scroll
        this.animatedScrollToTop();
      }
    },

    /**
     * Animated scroll to top (fallback for older browsers)
     */
    animatedScrollToTop() {
      const startPosition = window.pageYOffset;
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / this.scrollDuration, 1);

        // Easing function (ease-out cubic)
        const easeOut = 1 - Math.pow(1 - progress, 3);

        const newPosition = startPosition * (1 - easeOut);
        window.scrollTo(0, newPosition);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          this.onScrollComplete();
        }
      };

      if (this.prefersReducedMotion()) {
        // Skip animation for reduced motion preference
        window.scrollTo(0, 0);
        this.onScrollComplete();
      } else {
        requestAnimationFrame(animateScroll);
      }
    },

    /**
     * Monitor scroll completion for smooth scroll
     */
    monitorScrollCompletion() {
      let lastScrollTop = window.pageYOffset;
      let stableCount = 0;

      const checkCompletion = () => {
        const currentScrollTop = window.pageYOffset;

        if (currentScrollTop === lastScrollTop) {
          stableCount++;
          if (stableCount >= 5 || currentScrollTop === 0) {
            this.onScrollComplete();
            return;
          }
        } else {
          stableCount = 0;
        }

        lastScrollTop = currentScrollTop;

        // Continue monitoring
        setTimeout(checkCompletion, 50);
      };

      setTimeout(checkCompletion, 50);
    },

    /**
     * Handle scroll completion
     */
    onScrollComplete() {
      this.isScrolling = false;

      // Focus the main heading or skip link
      this.focusTopElement();

      // Announce completion
      this.announceScrollComplete();
    },

    /**
     * Focus the top element of the page
     */
    focusTopElement() {
      // Try to focus the main heading
      const mainHeading = document.querySelector('h1');
      if (mainHeading) {
        // Make it focusable if needed
        if (!mainHeading.hasAttribute('tabindex')) {
          mainHeading.setAttribute('tabindex', '-1');
        }
        mainHeading.focus();
        return;
      }

      // Fallback: focus skip navigation if available
      const skipLink = document.querySelector('.skip-navigation');
      if (skipLink) {
        skipLink.focus();
        return;
      }

      // Last resort: focus main content
      const mainContent = document.querySelector('main, [role="main"], #main-content');
      if (mainContent) {
        if (!mainContent.hasAttribute('tabindex')) {
          mainContent.setAttribute('tabindex', '-1');
        }
        mainContent.focus();
      }
    },

    /**
     * Announce scroll action to screen readers
     */
    announceScrollAction() {
      if (this.$announceSuccess) {
        this.$announceSuccess(this.$t('accessibility.scrolling_to_top', 'Scrolling to top of page'));
      }
    },

    /**
     * Announce scroll completion
     */
    announceScrollComplete() {
      if (this.$announceSuccess) {
        this.$announceSuccess(this.$t('accessibility.reached_top', 'Reached top of page'));
      }
    },

    /**
     * Check if user prefers reduced motion
     */
    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~/_variables.scss';

.back-to-top-container {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 1000;
}

.back-to-top-button {
  // Ensure minimum touch target size
  min-width: 56px;
  min-height: 56px;

  // Enhanced focus indicator
  &:focus {
    outline: 3px solid $white !important;
    outline-offset: 2px !important;
  }

  // Hover effects
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba($accent, 0.4) !important;
  }

  // Active state
  &:active {
    transform: translateY(0);
  }

  // Icon styling
  .v-icon {
    font-size: 1.25rem;
  }
}

.scroll-progress {
  position: absolute;
  right: -8px;
  top: 0;
  width: 4px;
  height: 56px;
  background: rgba($white, 0.3);
  border-radius: 2px;
  overflow: hidden;

  .progress-fill {
    width: 100%;
    background: $accent;
    border-radius: 2px;
    transition: height 0.1s ease-out;
    transform-origin: bottom;
  }
}

// Transition animations
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.9);
}

.fade-slide-enter-to,
.fade-slide-leave {
  opacity: 1;
  transform: translateY(0) scale(1);
}

// Mobile responsiveness
@media (max-width: 768px) {
  .back-to-top-container {
    bottom: 1rem;
    right: 1rem;
  }

  .back-to-top-button {
    // Slightly larger on mobile for easier touch
    min-width: 48px;
    min-height: 48px;
  }

  .scroll-progress {
    right: -6px;
    width: 3px;
    height: 48px;
  }
}

// High contrast mode
@media (prefers-contrast: high) {
  .back-to-top-button {
    background: #ffffff !important;
    color: #000000 !important;
    border: 2px solid #000000 !important;

    &:focus {
      outline: 3px solid #ffffff !important;
      outline-offset: 3px !important;
    }
  }

  .scroll-progress {
    background: #ffffff !important;
    border: 1px solid #000000 !important;

    .progress-fill {
      background: #000000 !important;
    }
  }
}

// Reduced motion
@media (prefers-reduced-motion: reduce) {
  .back-to-top-button {
    transition: none !important;

    &:hover {
      transform: none !important;
    }

    &:active {
      transform: none !important;
    }
  }

  .progress-fill {
    transition: none !important;
  }

  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity 0.1s ease !important;
  }

  .fade-slide-enter,
  .fade-slide-leave-to {
    transform: none !important;
  }

  .fade-slide-enter-to,
  .fade-slide-leave {
    transform: none !important;
  }
}

// Print styles
@media print {
  .back-to-top-container {
    display: none !important;
  }
}

// Large screens - adjust positioning
@media (min-width: 1400px) {
  .back-to-top-container {
    right: 2rem;
  }
}

// Landscape mobile orientation
@media (max-width: 768px) and (orientation: landscape) {
  .back-to-top-container {
    bottom: 0.5rem;
    right: 0.5rem;
  }

  .back-to-top-button {
    min-width: 44px;
    min-height: 44px;
  }
}
</style>
